
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Apollo } from '@/core/services';
import { generateAvatar } from '@/core/helpers/generateAvatar';
import { setPageHistory } from '@/core/helpers/toolbar';
import Table from '@/components/Table/Table.vue';
import InnerLoader from '@/components/InnerLoader.vue';
import AdvanceSearch from '@/components/search/AdvanceSearch.vue';
import { Select } from '@/components/input-elements';
import { GET_INACTIVE_COFFEE_CUSTOMERS_REPORT, SEARCH_INACTIVE_COFFEE_CUSTOMERS } from '../graphql/Queries';
import { GET_CUSTOMER_GROUP } from '@/modules/customer/customer-groups/graphql/Queries';

export default defineComponent({
    components: {
        Table,
        InnerLoader,
        AdvanceSearch,
        Select
    },
    setup() {
        const loader = ref(true);
        const store = useStore();
        const router = useRouter();
        const i18n = useI18n();
        const pagination = ref({}) as Record<any, any>;
        const reports: any = ref([]);
        const clickHandler = ref() as Record<any, any>;
        const activeIndex = ref(-1);
        const system_locale = ref();
        const columnName = ref(null);
        const columnSort = ref(null);
        const selectedMonths = ref(1);
        const rentalAgreementOnly = ref(true);
        const customerGroupList = ref([]) as Record<any, any>;

        const monthsOptions = ref([
            { label: 'message.ONE_MONTH', value: 1 },
            { label: 'message.TWO_MONTHS', value: 2 },
            { label: 'message.THREE_MONTHS', value: 3 }
        ]);

        const filterOption = ref({
            name: true,
            email: true,
            customer_group: true,
            status: true
        });

        const columns = ref([
            { label: '', key: 'avatar' },
            { label: 'message.NAME', key: 'name', sorting: true },
            { label: 'message.EMAIL', key: 'email', sorting: true },
            { label: 'message.ECONOMIC_NUMBER', key: 'economic_number', sorting: true },
            { label: 'message.CUSTOMER_GROUP', key: 'customer_group' },
            { label: 'message.LAST_COFFEE_ORDER_DATE', key: 'last_coffee_order_date', sorting: true },
            { label: 'message.DAYS_SINCE_LAST_COFFEE_ORDER', key: 'days_since_last_coffee_order', sorting: true },
            { label: 'message.CUP_AGREEMENT', key: 'in_cup_agreement' },
            { label: 'message.STATUS', key: 'status' },
            { label: 'message.ACTIONS', key: 'actions', textAlignment: 'center' }
        ]);

        const statusList = ref([
            { label: 'message.ALL', value: 'all' },
            { label: 'message.ACTIVE', value: 1 },
            { label: 'message.INACTIVE', value: 0 }
        ]);

        const reportVariables = (page = 1, colName = null) => ({
            page,
            limit: 10,
            months: Number(selectedMonths.value) || 1,
            rental_agreement_only: rentalAgreementOnly.value,
            col: colName,
            type: columnSort.value == true ? 'asc' : 'desc'
        });

        const getCustomerGroups = () => {
            Apollo.watchQuery({
                query: GET_CUSTOMER_GROUP,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only'
            }).subscribe(({ data }) => {
                customerGroupList.value = [];
                data.customer_groups.data.forEach(ele => {
                    customerGroupList.value.push({
                        label: ele.name,
                        value: ele.id
                    });
                });
            });
        };

        const getInactiveCoffeeCustomersReport = (page = 1, reload = false, colName = null, type = null) => {
            if (colName != null) columnName.value = colName;
            if (type != null) columnSort.value = type;
            loader.value = true;

            const sub = Apollo.watchQuery({
                query: GET_INACTIVE_COFFEE_CUSTOMERS_REPORT,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: reportVariables(page, columnName.value)
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                reload == true ? null : null;
                clickHandler.value = getInactiveCoffeeCustomersReport;
                reports.value = data?.inactive_coffee_customers_report?.data;
                pagination.value = data?.inactive_coffee_customers_report;
                loader.value = false;
                sub.unsubscribe();
            });
        };

        const searchHandler = (page = 1, reload = false, colName = null, type = null) => {
            if (colName != null) columnName.value = colName;
            if (type != null) columnSort.value = type;
            const searchData = store.getters.getSearchData;
            loader.value = true;

            const search = Apollo.watchQuery({
                query: SEARCH_INACTIVE_COFFEE_CUSTOMERS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    ...reportVariables(page, columnName.value),
                    filter: JSON.stringify(searchData?.data?.select),
                    search_key: searchData?.data?.input,
                    locale: system_locale.value
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                clickHandler.value = searchHandler;
                reports.value = data?.search_inactive_coffee_customers?.data;
                pagination.value = data?.search_inactive_coffee_customers;
                loader.value = false;
                search.unsubscribe();
            });
        };

        const reloadReportListing = () => {
            store.commit('SET_SEARCH_DATA', {});
            setPageHistory({});
            getInactiveCoffeeCustomersReport(1, true);
        };

        const exportFile = data => {
            const searchData = store.getters.getSearchData;
            loader.value = true;
            const sub = Apollo.watchQuery({
                query: SEARCH_INACTIVE_COFFEE_CUSTOMERS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: {
                    ...reportVariables(0),
                    filter: searchData?.data?.select ? JSON.stringify(searchData.data.select) : '',
                    search_key: searchData?.data?.input ? searchData?.data?.input : '',
                    types: data?.type,
                    locale: system_locale.value
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                } else if (data) {
                    window.open(data?.search_inactive_coffee_customers?.data[0]?.path, '_blank');
                    loader.value = false;
                }
                loader.value = false;
                sub.unsubscribe();
            });
        };

        const handleView = (val: Record<any, any>) => {
            const searchData = store.getters.getSearchData;
            setPageHistory({
                filter: searchData?.data?.select,
                search_key: searchData?.data?.input,
                page_number: pagination.value?.current_page
            });

            router.push({
                name: 'customer_detail',
                params: { uuid: val.uuid, orderTab: 'true' }
            });
        };

        const handleCurrentChange = (page: number) => {
            clickHandler.value(page);
        };

        const dropdownMenuStyle = ref({});
        const dropdownMenuRef = ref<HTMLElement | null>(null);
        const dropdownIsDark = ref(false);
        let dropdownButton: HTMLElement | null = null;
        const activeReport = computed(() => reports.value?.find((report: any) => report?.id === activeIndex.value) || null);

        const closeDropdown = () => {
            activeIndex.value = -1;
            dropdownButton = null;
            dropdownMenuStyle.value = {};
        };

        const placeDropdown = (button: HTMLElement) => {
            const menu = dropdownMenuRef.value;
            if (!menu) return;
            const rect = button.getBoundingClientRect();
            const menuHeight = menu.offsetHeight || 160;
            const menuWidth = menu.offsetWidth || 160;
            const gap = 6;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUp = spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;
            let top = openUp ? rect.top - menuHeight - gap : rect.bottom + gap;
            let left = rect.right - menuWidth;

            if (left < 8) left = 8;
            if (left + menuWidth > window.innerWidth - 8) left = Math.max(8, window.innerWidth - menuWidth - 8);
            if (top < 8) top = 8;
            if (top + menuHeight > window.innerHeight - 8) top = Math.max(8, window.innerHeight - menuHeight - 8);

            dropdownMenuStyle.value = {
                display: 'block',
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                right: 'auto',
                bottom: 'auto',
                zIndex: 2000,
                margin: 0
            };
        };

        const dropdownIsVisible = (button: HTMLElement) => {
            const container = button.closest('.table-responsive');
            if (!container) return true;
            const buttonRect = button.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            return buttonRect.bottom > containerRect.top && buttonRect.top < containerRect.bottom && buttonRect.right > containerRect.left && buttonRect.left < containerRect.right;
        };

        const syncDropdownPosition = () => {
            if (!dropdownButton) return;
            if (!dropdownIsVisible(dropdownButton)) {
                closeDropdown();
                return;
            }
            placeDropdown(dropdownButton);
        };

        const dropdownHandler = (index, id, event) => {
            if (index === id) {
                closeDropdown();
                return;
            }
            activeIndex.value = id;
            dropdownButton = event.currentTarget;
            dropdownIsDark.value = !!document.querySelector('.layout-dark');
            nextTick(() => {
                if (dropdownButton) placeDropdown(dropdownButton);
            });
        };

        const handleClickOutside = event => {
            if (!event.target.closest('.action-btn') && !event.target.closest('.inactive-coffee-action-menu')) {
                closeDropdown();
            }
        };

        watch([selectedMonths, rentalAgreementOnly], () => {
            const searchData = store.getters.getSearchData;
            if (searchData?.data?.select || searchData?.data?.input) {
                searchHandler(1);
            } else {
                getInactiveCoffeeCustomersReport(1, true);
            }
        });

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
        });

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
            window.addEventListener('resize', syncDropdownPosition);
            document.addEventListener('scroll', syncDropdownPosition, true);
            getCustomerGroups();
            getInactiveCoffeeCustomersReport();
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
            window.removeEventListener('resize', syncDropdownPosition);
            document.removeEventListener('scroll', syncDropdownPosition, true);
        });

        return {
            generateAvatar,
            reports,
            columns,
            dropdownHandler,
            closeDropdown,
            activeIndex,
            activeReport,
            dropdownMenuStyle,
            dropdownMenuRef,
            dropdownIsDark,
            pagination,
            filterOption,
            statusList,
            customerGroupList,
            handleView,
            searchHandler,
            reloadReportListing,
            exportFile,
            loader,
            handleCurrentChange,
            selectedMonths,
            rentalAgreementOnly,
            monthsOptions
        };
    }
});
